<template>
  <div class="dte-lines-editor">
    <!-- Toolbar -->
    <div class="d-flex align-center mb-3">
      <v-avatar color="accent" size="28" class="mr-2">
        <v-icon color="white" x-small>fas fa-boxes</v-icon>
      </v-avatar>
      <span class="caption font-weight-black text-uppercase tracking-widest primary--text">
        Detalle de Productos y Servicios
      </span>
      <v-spacer />
      <v-btn
        color="accent"
        small
        class="font-weight-bold shadow-xs"
        @click="addLine"
      >
        <v-icon left x-small>fas fa-plus</v-icon>
        Agregar Línea
      </v-btn>
    </div>

    <!-- Tabla editable -->
    <v-card outlined class="overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="internalLines"
        item-key="nro_linea"
        hide-default-footer
        :items-per-page="-1"
        class="dte-table"
      >
        <template v-slot:no-data>
          <div class="pa-8 text-center grey--text">
            <v-icon size="40" color="grey lighten-3" class="mb-3">fas fa-receipt</v-icon>
            <div class="body-2 font-weight-bold mb-1">Sin productos en el documento</div>
            <div class="caption">Haz clic en "Agregar Línea" para comenzar</div>
          </div>
        </template>

        <!-- SKU -->
        <template v-slot:item.sku="{ item, index }">
          <v-text-field
            v-model="item.sku"
            :disabled="item._skuChecking"
            outlined dense hide-details
            class="dte-input-minimal font-weight-black accent--text"
            :class="{
              'sku-valid': item._skuValid === true,
              'sku-invalid': item._skuValid === false,
            }"
            placeholder="SKU..."
            @blur="onSkuBlur(item)"
          >
            <template v-slot:append>
              <v-progress-circular
                v-if="item._skuChecking"
                indeterminate
                size="14"
                width="2"
                color="grey lighten-1"
                class="mt-1"
              />
              <v-tooltip v-else-if="item._skuValid === true" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small color="success" v-bind="attrs" v-on="on">fas fa-check-circle</v-icon>
                </template>
                <span>Confirmado en Relbase ({{ item.relbase_product_id }})</span>
              </v-tooltip>
              <v-tooltip v-else-if="item._skuValid === false" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small color="error" v-bind="attrs" v-on="on">fas fa-times-circle</v-icon>
                </template>
                <span>
                  <span v-if="!item.relbase_product_id">SKU no encontrado en Relbase</span>
                  <span v-else-if="!item.name || !item.name.trim()">Descripción vacía</span>
                  <span v-else-if="!(item.qty > 0)">Cantidad debe ser mayor a 0</span>
                </span>
              </v-tooltip>
            </template>
          </v-text-field>
        </template>

        <!-- Descripción -->
        <template v-slot:item.name="{ item }">
          <v-textarea
            v-model="item.name"
            rows="1"
            auto-grow
            outlined dense hide-details
            class="dte-input-minimal body-2 py-1"
            :class="{
              'sku-valid': item._skuValid !== null && !!item.name && !!item.name.trim(),
              'sku-invalid': item._skuValid !== null && (!item.name || !item.name.trim()),
            }"
            placeholder="Descripción..."
            @input="emitChange"
            @blur="revalidateSync(item)"
          />
        </template>

        <!-- Cantidad -->
        <template v-slot:item.qty="{ item }">
          <v-text-field
            v-model.number="item.qty"
            type="number"
            :min="0"
            outlined dense hide-details
            class="dte-input-minimal text-center"
            :class="{
              'sku-valid': item._skuValid !== null && item.qty > 0,
              'sku-invalid': item._skuValid !== null && !(item.qty > 0),
            }"
            @input="emitChange"
            @blur="revalidateSync(item)"
          />
        </template>

        <!-- Precio Unitario -->
        <template v-slot:item.price="{ item }">
          <v-text-field
            v-model.number="item.price"
            type="number"
            :min="0"
            outlined dense hide-details
            class="dte-input-minimal text-right"
            @input="emitChange"
          />
        </template>

        <!-- Toggle Afecto / Exento IVA -->
        <template v-slot:item.afecto="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
                @click="toggleAfecto(item)"
              >
                <v-icon
                  small
                  :color="item.afecto ? 'accent' : 'grey lighten-1'"
                >
                  {{ item.afecto ? 'fas fa-check-circle' : 'fas fa-circle' }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ item.afecto ? 'Afecto IVA' : 'Exento IVA' }}</span>
          </v-tooltip>
        </template>

        <!-- Total de Línea -->
        <template v-slot:item.line_total="{ item }">
          <span class="body-2 font-weight-black primary--text">
            {{ lineTotal(item) | moneda }}
          </span>
        </template>

        <!-- Acciones -->
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon small color="error"
            class="grey lighten-4"
            @click="removeLine(item)"
          >
            <v-icon x-small>fas fa-trash</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Totales + Descuento Global -->
    <v-card outlined class="mt-4 pa-5 totals-card">
      <v-row align="center" no-gutters>

        <!-- Neto bruto -->
        <v-col cols="12" sm="3" class="text-center px-3 mb-3 mb-sm-0">
          <div class="caption grey--text font-weight-black text-uppercase mb-1">Neto Bruto</div>
          <div class="text-h6 font-weight-black primary--text">{{ netoAfectoBruto | moneda }}</div>
          <div v-if="netoExento > 0" class="caption grey--text mt-1">
            + {{ netoExento | moneda }} <span class="font-weight-bold">exento</span>
          </div>
        </v-col>

        <!-- Descuento global -->
        <v-col cols="12" sm="3" class="text-center px-3 mb-3 mb-sm-0 border-lr">
          <div class="caption grey--text font-weight-black text-uppercase mb-1">Descuento Global</div>
          <v-text-field
            v-model.number="internalDiscount"
            type="number"
            :min="0"
            :max="100"
            outlined dense hide-details
            suffix="%"
            class="dte-input-minimal text-center discount-input mx-auto"
            style="max-width: 110px"
            @input="emitDiscountChange"
          />
          <div v-if="internalDiscount > 0" class="caption error--text font-weight-bold mt-1">
            − {{ discountAmount | moneda }}
          </div>
        </v-col>

        <!-- IVA -->
        <v-col cols="12" sm="3" class="text-center px-3 mb-3 mb-sm-0 border-right">
          <div class="caption grey--text font-weight-black text-uppercase mb-1">
            IVA ({{ taxRate }}%)
          </div>
          <div class="text-h6 font-weight-bold grey--text text--darken-2">{{ ivaAmount | moneda }}</div>
        </v-col>

        <!-- Total final -->
        <v-col cols="12" sm="3" class="text-center px-3">
          <div class="caption accent--text font-weight-black text-uppercase mb-1">Total</div>
          <div class="text-h5 font-weight-black primary--text">{{ totalAmount | moneda }}</div>
        </v-col>

      </v-row>
    </v-card>
  </div>
</template>

<script>
import { lookupProductInRelbase } from '@/api/relbase'

export default {
  name: 'DTELinesEditor',

  props: {
    value: {
      type: Array,
      default: () => [],
    },
    globalDiscount: {
      type: Number,
      default: 0,
    },
    taxRate: {
      type: Number,
      default: 19,
    },
  },

  data() {
    return {
      internalLines: [],
      internalDiscount: this.globalDiscount,
      headers: [
        { text: 'Nro', value: 'nro_linea', sortable: false, width: '10px', class: 'dte-th' },
        { text: 'SKU', value: 'sku', sortable: false, width: '150px', class: 'dte-th' },
        { text: 'Descripción', value: 'name', sortable: false, class: 'dte-th' },
        { text: 'Cant.', value: 'qty', sortable: false, width: '110px', class: 'dte-th' },
        { text: 'Precio Unit.', value: 'price', sortable: false, width: '130px', class: 'dte-th' },
        { text: 'IVA', value: 'afecto', sortable: false, width: '60px', class: 'dte-th text-center' },
        { text: 'Total Línea', value: 'line_total', sortable: false, width: '120px', class: 'dte-th text-right' },
        { text: '', value: 'actions', sortable: false, width: '46px', class: 'dte-th' },
      ],
    }
  },

  computed: {
    allLinesValid() {
      if (this.internalLines.length === 0) return false
      return this.internalLines.every(l => l._skuValid === true)
    },
    anyChecking() {
      return this.internalLines.some(l => l._skuChecking)
    },
    netoAfectoBruto() {
      return this.internalLines
        .filter(l => l.afecto !== false)
        .reduce((acc, l) => acc + this.lineTotal(l), 0)
    },
    netoExento() {
      return this.internalLines
        .filter(l => l.afecto === false)
        .reduce((acc, l) => acc + this.lineTotal(l), 0)
    },
    discountAmount() {
      const disc = parseFloat(this.internalDiscount || 0)
      return Math.round((this.netoAfectoBruto + this.netoExento) * disc / 100)
    },
    netoAfectoConDescuento() {
      const disc = parseFloat(this.internalDiscount || 0)
      return Math.round(this.netoAfectoBruto * (1 - disc / 100))
    },
    netoExentoConDescuento() {
      const disc = parseFloat(this.internalDiscount || 0)
      return Math.round(this.netoExento * (1 - disc / 100))
    },
    ivaAmount() {
      return Math.round(this.netoAfectoConDescuento * this.taxRate * 0.01)
    },
    totalAmount() {
      return this.netoAfectoConDescuento + this.netoExentoConDescuento + this.ivaAmount
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        const incoming = val || []
        // Rebuild internalLines preserving _skuValid for lines whose SKU hasn't changed
        this.internalLines = incoming.map((l, i) => {
          const existing = this.internalLines[i]
          const sameSku = existing && existing.sku === (l.sku ?? '')
          const base = this.normalizeLineItem(l, i)
          if (sameSku) {
            base._skuValid = existing._skuValid
            base._skuChecking = existing._skuChecking
          }
          return base
        })
        // Validate any lines that have a SKU but no validation result yet
        this.$nextTick(() => this._validatePendingLines())
      },
    },
    allLinesValid(val) {
      this.$emit('valid-change', val)
    },
    globalDiscount(val) {
      this.internalDiscount = val
    },
  },

  methods: {
    normalizeLineItem(l, index) {
      return {
        nro_linea: l.nro_linea ?? index + 1,
        product: l.product ?? -1,
        sku: l.sku ?? '',
        name: l.name ?? '',
        unit: l.unit ?? 'UN',
        qty: l.qty ?? 0,
        price: l.price ?? 0,
        afecto: l.afecto !== undefined ? l.afecto : true,
        relbase_product_id: l.relbase_product_id ?? null,
        _skuChecking: false,
        _skuValid: null,
      }
    },

    _validatePendingLines() {
      this.internalLines.forEach((line, index) => {
        if (line.sku && line._skuValid === null && !line._skuChecking) {
          this._lookupSku(index)
        }
      })
    },

    onSkuBlur(item) {
      if (!item.sku) return
      // Use nro_linea to find real index (v-data-table index ≠ array index)
      const idx = this.internalLines.findIndex(l => l.nro_linea === item.nro_linea)
      if (idx === -1) return
      this.internalLines[idx]._skuValid = null
      this._lookupSku(idx)
    },

    async _lookupSku(index) {
      const line = this.internalLines[index]
      if (!line || !line.sku) return

      const nroLinea = line.nro_linea
      const skuAtStart = line.sku

      // Show loading & disable input
      this.internalLines[index]._skuChecking = true
      this.internalLines[index]._skuValid = null

      const result = await lookupProductInRelbase(skuAtStart)

      // Find line by nro_linea to handle index shifts from add/remove
      const idx = this.internalLines.findIndex(
        l => l.nro_linea === nroLinea && l.sku === skuAtStart
      )
      if (idx === -1) return  // Line removed or SKU changed while loading

      const current = this.internalLines[idx]
      current._skuChecking = false

      if (result.found) {
        current.relbase_product_id = result.relbase_id
        // Auto-fill name only if the field is empty
        if (!current.name && result.name) {
          current.name = result.name
        }
      } else {
        current.relbase_product_id = null
      }

      // SKU validity = Relbase found AND name AND qty
      current._skuValid = this._checkLineValid(current)

      // Propagate relbase_product_id change to parent
      this.emitChange()
    },

    _checkLineValid(line) {
      return !!(line.relbase_product_id && line.name && line.name.trim() && line.qty > 0)
    },

    revalidateSync(item) {
      // Re-check validity without API call (SKU was already looked up)
      const idx = this.internalLines.findIndex(l => l.nro_linea === item.nro_linea)
      if (idx === -1) return
      const line = this.internalLines[idx]
      // Only re-validate if the line has already gone through SKU validation
      if (line._skuValid !== null) {
        line._skuValid = this._checkLineValid(line)
      }
    },

    addLine() {
      this.internalLines.push(this.normalizeLineItem({}, this.internalLines.length))
      this.emitChange()
    },

    removeLine(item) {
      console.log("removeLine", item)
      const idx = this.internalLines.findIndex(l => l.nro_linea === item.nro_linea)
      this.internalLines.splice(idx, 1)
      this.emitChange()
    },

    toggleAfecto(item) {
      item.afecto = !item.afecto
      this.emitChange()
    },

    lineTotal(item) {
      return Math.round(parseFloat(item.qty || 0) * parseFloat(item.price || 0))
    },

    emitChange() {
      // Strip internal validation state before emitting to parent
      const clean = this.internalLines.map(({ _skuChecking, _skuValid, ...line }) => line)
      this.$emit('input', clean)
    },

    emitDiscountChange() {
      this.$emit('update:globalDiscount', this.internalDiscount)
    },
  },
}
</script>

<style scoped>
.dte-table >>> thead th.dte-th {
  background-color: #f0f4f7 !important;
  text-transform: uppercase;
  font-size: 0.72rem !important;
  letter-spacing: 1px;
  font-weight: 900 !important;
  color: #546e7a !important;
  height: 48px !important;
}
.dte-table >>> td {
  height: 58px !important;
  border-bottom: 1px solid #eef2f5 !important;
}

.dte-input-minimal >>> .v-input__control fieldset {
  border-radius: 6px !important;
  border-color: #cbd5e1 !important;
}
.dte-input-minimal >>> .v-input__slot {
  min-height: 38px !important;
}
.dte-input-minimal >>> input,
.dte-input-minimal >>> textarea {
  font-size: 0.875rem !important;
  padding: 6px 8px !important;
}

.sku-valid >>> .v-input__control fieldset {
  border-color: #4caf50 !important;
}
.sku-invalid >>> .v-input__control fieldset {
  border-color: #f44336 !important;
}

.totals-card {
  background: linear-gradient(135deg, #f8fafc 0%, #eef6f7 100%);
  border-radius: 8px !important;
}

.border-lr {
  border-left: 1px solid rgba(0,0,0,0.06);
  border-right: 1px solid rgba(0,0,0,0.06);
}
.border-right {
  border-right: 1px solid rgba(0,0,0,0.06);
}

.discount-input >>> .v-input__slot { min-height: 36px !important; }

.tracking-widest { letter-spacing: 1.8px; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
</style>
